import React, {useEffect, useState} from 'react';
import {
  FlatList,
  Image,
  StyleSheet,
  useWindowDimensions,
  View,
} from 'react-native';
import {CountdownCircleTimer} from 'react-native-countdown-circle-timer';

import {Badge} from '../../components/article/priceBadges';
import {getStickerTextParts} from '../../components/ChevronMotif';
import LoadingImg from '../../components/LoadingImg';
import ContentView from '../../components/ui-controls/layout/ContentView';
import Text from '../../components/ui-controls/Text';
import BeerBoardHeaderImg from '../../images/brand/beerboard/header.png';
import {useStores} from '../../stores/use-stores';
import theme from '../../style/theme';
import apiService, {
  GetDepartmentLinesRequestParams,
  GetDepartmentLinesSortOptionId,
} from '../../transport/apiService';
import {OrderLine, OrderLineSticker} from '../../transport/apiServiceResponse';
import {arrayChunks, toAUD} from '../../utils/functions';
import {makeABVText, makeByLineText} from '../../utils/products';

import BeerBoardHelp from './BeerBoardHelp';
import {
  isBeerCase,
  isBeerDepartment,
  isSavingOrSpecialSticker,
  isSavingsOrSpecial,
} from './utils';

const DefaultParams = {
  dwellTime: 20,
  filter: undefined,
  productsPerPage: 10,
  sort: 'top_products',
};

const getURLParams = () => {
  const url = new URL(window?.location?.href);
  const pathname = url.pathname;
  const pathParts = pathname.split('/');
  const shopSalesforceId = pathParts[2];

  const params = new URLSearchParams(url.search);
  const isHelp = !!params.get('help');
  const sort = params.get('sort') || DefaultParams.sort;
  const filter =
    (params.get('filter') as 'specials_only') || DefaultParams.filter;
  const productsPerPage = params.get('productsPerPage')
    ? Number(params.get('productsPerPage'))
    : DefaultParams.productsPerPage;
  const dwellTime = params.get('dwellTime')
    ? Number(params.get('dwellTime'))
    : DefaultParams.dwellTime;
  return {
    dwellTime,
    filter,
    isHelp,
    pathname,
    productsPerPage,
    shopSalesforceId,
    sort,
  };
};

const fetchBeerCases = async ({
  beerDepartmentId,
  sortOptionId,
  isSpecialsOnly,
}: {
  beerDepartmentId: string;
  sortOptionId?: GetDepartmentLinesSortOptionId;
  isSpecialsOnly: boolean;
}) => {
  const shopDepartmentLinesParams: GetDepartmentLinesRequestParams = {
    department_id: beerDepartmentId,
    sort_option_id: sortOptionId,
    facets: isSpecialsOnly ? ['special:drill'] : undefined,
    page_size: 200,
  };
  const apiResponse = await apiService.getDepartmentLines(
    shopDepartmentLinesParams,
  );
  const beerProducts = apiResponse.data as OrderLine[];
  if (!beerProducts.length) throw new Error('No beer products found');

  const beerCaseProducts = beerProducts.filter(isBeerCase);

  return beerCaseProducts;
};

const BeerBoardScreen = () => {
  const {shopSelectionStore, rangeStore} = useStores();

  const {
    shopSalesforceId,
    filter,
    sort,
    productsPerPage,
    dwellTime,
    isHelp,
    pathname,
  } = getURLParams();
  const sortOptionId = sort as GetDepartmentLinesSortOptionId;
  const isSpecialsOnly = filter === 'specials_only';

  const [products, setProducts] = useState<OrderLine[]>([]);
  const [error, setError] = useState('');

  useEffect(() => {
    async function loadBeerBoardData() {
      try {
        if (!shopSalesforceId) throw new Error('No shop id provided');

        await shopSelectionStore.findSalesforceShop(shopSalesforceId);
        if (!shopSelectionStore.selectedStore)
          throw new Error('Could not find the store with the provided id');

        const beerDepartment = rangeStore.departments.find(isBeerDepartment);
        if (!beerDepartment)
          throw new Error('Could not find the Beer department');

        const beerCaseProducts = await fetchBeerCases({
          beerDepartmentId: beerDepartment.id,
          sortOptionId,
          isSpecialsOnly,
        });

        setProducts(beerCaseProducts);
      } catch (err: any) {
        setError(err.message);
      }
    }

    loadBeerBoardData();
    // only once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isHelp) return <BeerBoardHelp baseUrl={pathname} />;

  if (error) {
    return (
      <ContentView>
        <Text>{error}</Text>
      </ContentView>
    );
  }

  return products.length ? (
    <BeerBoard
      products={products}
      productsPerPage={productsPerPage}
      dwellTime={dwellTime}
    />
  ) : (
    <ContentView>
      <LoadingImg />
    </ContentView>
  );
};

enum ScreenResolution {
  HD = 'HD',
  FHD = 'FHD',
  UHD = 'UHD',
}

// The board screens are vertical
const ScreenResolutionMeasurement = {
  [ScreenResolution.HD]: {
    width: 720,
    height: 1280,
  },
  [ScreenResolution.FHD]: {
    width: 1080,
    height: 1920,
  },
  [ScreenResolution.UHD]: {
    width: 2160,
    height: 3840,
  },
};

const Sizes = {
  [ScreenResolution.HD]: {
    ScreenVerticalPadding: 20,
    ScreenHorizontalPadding: 30,
    VerticalSpacing: 20,
    HorizontalSpacing: 20,
    HeaderHeight: 80,
    FooterHeight: 50,
    FontPrimary: 28,
    FontPrice: 32,
    FontSecondary: 20,
    TimerStrokeWidth: 5,
  },
  [ScreenResolution.FHD]: {
    ScreenVerticalPadding: 20,
    ScreenHorizontalPadding: 30,
    VerticalSpacing: 18,
    HorizontalSpacing: 20,
    HeaderHeight: 100,
    FooterHeight: 70,
    FontPrimary: 38,
    FontPrice: 42,
    FontSecondary: 24,
    TimerStrokeWidth: 6,
  },
  [ScreenResolution.UHD]: {
    ScreenVerticalPadding: 40,
    ScreenHorizontalPadding: 60,
    VerticalSpacing: 20,
    HorizontalSpacing: 20,
    HeaderHeight: 160,
    FooterHeight: 140,
    FontPrimary: 64,
    FontPrice: 68,
    FontSecondary: 48,
    TimerStrokeWidth: 12,
  },
};

const useResponsiveSizes = () => {
  const {width} = useWindowDimensions();
  if (width <= ScreenResolutionMeasurement[ScreenResolution.HD].width) {
    return Sizes[ScreenResolution.HD];
  } else if (width <= ScreenResolutionMeasurement[ScreenResolution.FHD].width) {
    return Sizes[ScreenResolution.FHD];
  } else {
    return Sizes[ScreenResolution.UHD];
  }
};

const useResponsiveStyles = () => {
  const responsiveSizes = useResponsiveSizes();
  const fontColour = 'white';

  const styles = StyleSheet.create({
    main: {
      flex: 1,
      backgroundColor: theme.colour.background.general.primary,
      paddingVertical: responsiveSizes.ScreenVerticalPadding,
      paddingHorizontal: responsiveSizes.ScreenHorizontalPadding,
    },
    header: {
      height: responsiveSizes.HeaderHeight,
      resizeMode: 'contain',
      marginBottom: responsiveSizes.VerticalSpacing,
    },
    footer: {
      height: responsiveSizes.FooterHeight,
      flexDirection: 'row',
      justifyContent: 'center',
      marginTop: responsiveSizes.VerticalSpacing,
    },
    footerText: {
      color: fontColour,
      fontSize: responsiveSizes.FontSecondary,
    },
    beerCaseItem: {
      justifyContent: 'space-between',
      flexDirection: 'row',
      gap: responsiveSizes.HorizontalSpacing,
    },
    beerCaseItemTitleText: {
      fontSize: responsiveSizes.FontPrimary,
      color: fontColour,
      marginBottom: responsiveSizes.VerticalSpacing,
    },
    beerCaseItemSecondaryText: {
      fontSize: responsiveSizes.FontSecondary,
      color: fontColour,
    },
    beerCaseItemPriceText: {
      fontSize: responsiveSizes.FontPrice,
      color: fontColour,
    },
  });
  return styles;
};

const BeerBoardHeader = () => (
  <Image source={BeerBoardHeaderImg} style={useResponsiveStyles().header} />
);

type BeerBoardFooterProps = {
  onTimerComplete: () => void;
  timerDuration?: number;
  timerText: string;
};
const BeerBoardFooter = ({
  onTimerComplete,
  timerDuration = 20,
  timerText,
}: BeerBoardFooterProps) => {
  const handleOnComplete = () => {
    onTimerComplete();
    return {shouldRepeat: true};
  };
  const colour = theme.colour.general.secondary as `#${string}`;
  const styles = useResponsiveStyles();
  const sizes = useResponsiveSizes();
  const timerContent = () => <Text style={styles.footerText}>{timerText}</Text>;
  return (
    <View style={styles.footer}>
      <CountdownCircleTimer
        colors={[colour, colour]}
        colorsTime={[timerDuration, 0]}
        duration={timerDuration}
        isPlaying
        onComplete={handleOnComplete}
        size={styles.footer.height}
        strokeWidth={sizes.TimerStrokeWidth}>
        {timerContent}
      </CountdownCircleTimer>
    </View>
  );
};

type BeerCaseItemProps = {
  product: OrderLine;
  height: number;
  hasSpecialsColumn: boolean;
};
const BeerCaseItem = ({
  product,
  height,
  hasSpecialsColumn,
}: BeerCaseItemProps) => {
  const styles = useResponsiveStyles();

  const stickerWidth = height / 1.5;

  const renderBadge = (sticker: OrderLineSticker) => (
    <Badge
      key={product.product_id}
      size={stickerWidth}
      {...getStickerTextParts(sticker)}
    />
  );

  return (
    <View style={[{height}, styles.beerCaseItem]} testID="beer-case-item">
      <Image
        style={{height, width: height}}
        source={{uri: product.links.medium_image}}
      />
      <View style={{flex: 1, justifyContent: 'center'}}>
        {/*  Vertical centering */}
        <View>
          <Text style={styles.beerCaseItemTitleText}>{product.name}</Text>
          <Text style={styles.beerCaseItemSecondaryText} variant={'caption'}>
            {makeByLineText(product)} - {makeABVText(product)}
          </Text>
        </View>
      </View>
      {!!hasSpecialsColumn && (
        <View
          style={{height, width: stickerWidth}}
          testID="beer-case-item-sticker-container">
          <View
            style={{height, flex: 1, justifyContent: 'center'}}
            testID="beer-case-item-sticker-centering-container">
            {/*  Vertical centering */}
            <View
              style={{width: stickerWidth}}
              testID="beer-case-item-sticker-centered-container">
              {product.stickers
                ?.filter(isSavingOrSpecialSticker)
                .map(renderBadge)}
            </View>
          </View>
        </View>
      )}
      <View style={{height, justifyContent: 'center'}}>
        <Text style={[{textAlign: 'right'}, styles.beerCaseItemPriceText]}>
          {toAUD(product.sell_price_cents)}
        </Text>
      </View>
    </View>
  );
};

const useResponsiveBeerCaseItemHeight = (productsPerPage: number) => {
  const {height} = useWindowDimensions();
  const {HeaderHeight, FooterHeight, ScreenVerticalPadding, VerticalSpacing} =
    useResponsiveSizes();
  return (
    (height -
      HeaderHeight -
      FooterHeight -
      ScreenVerticalPadding * 2 - // top + bottom
      VerticalSpacing * 2) / // header.marginBottom + footer.marginTop
    productsPerPage
  );
};

type BeerBoardProps = {
  products: OrderLine[];
  productsPerPage: number;
  dwellTime: number;
};
const BeerBoard = ({products, productsPerPage, dwellTime}: BeerBoardProps) => {
  const styles = useResponsiveStyles();
  const beerCaseItemHeight = useResponsiveBeerCaseItemHeight(productsPerPage);

  const productPages = arrayChunks(products, productsPerPage);
  const [currentProductPage, setCurrentProductPage] = useState(productPages[0]);
  const [currentProductPageNumber, setCurrentProductPageNumber] = useState(0);

  const handleTimerComplete = () => {
    if (currentProductPageNumber === productPages.length - 1) {
      setCurrentProductPageNumber(0);
      setCurrentProductPage(productPages[0]);
    } else {
      setCurrentProductPageNumber(currentProductPageNumber + 1);
      setCurrentProductPage(productPages[currentProductPageNumber + 1]);
    }
  };

  const hasSpecials = currentProductPage.some(isSavingsOrSpecial);
  const renderBeerBoardListItem = ({item}: {item: OrderLine}) => (
    <BeerCaseItem
      product={item}
      height={beerCaseItemHeight}
      hasSpecialsColumn={hasSpecials}
    />
  );

  const footerTimerText = `${currentProductPageNumber + 1}/${
    productPages.length
  }`;

  return (
    <View style={styles.main}>
      <BeerBoardHeader />
      <FlatList
        data={currentProductPage}
        renderItem={renderBeerBoardListItem}
      />
      {productPages.length > 1 && (
        <BeerBoardFooter
          onTimerComplete={handleTimerComplete}
          timerDuration={dwellTime}
          timerText={footerTimerText}
        />
      )}
    </View>
  );
};

export default BeerBoardScreen;
